import { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../types/global/routes.types';
import { getJWtDetails } from '../utils/AppUtils';
import { useDispatch, useSelector } from 'react-redux';
import {
    isAdminSelector,
    isUserCastingDirectorSelector,
    isUserJurySelector,
    userPermissionsSelector,
} from '../selectors/selectors';
import { E_USER_PERMISSIONS, UserPermissionLevels } from '../types/global/user.models';
import { refreshToken } from '../redux/refreshToken/action';
import { toggleAuthModal } from '../redux/authentication/action';
import type { RootState } from '../redux/root-reducer';

interface IProtectedRoute {
    children: JSX.Element;
    accessCheck?: boolean;
    accessType?: UserPermissionLevels | UserPermissionLevels[]; // Support single or multiple
    /** Opens sign-in modal and keeps the current URL (e.g. /projects?tab=playlists) */
    showAuthModal?: boolean;
}

export const ProtectedRoute = ({ children, accessCheck, accessType, showAuthModal }: IProtectedRoute) => {
    const { isSignedIn } = getJWtDetails();
    const location = useLocation();
    const dispatch = useDispatch();
    const refreshingToken = useSelector((state: RootState) => state.tokenRefresh.refreshingToken);
    const storedRefreshToken = useSelector((state: RootState) => state.authentication.refreshToken);
    const isJuryEnabled = useSelector(isUserJurySelector);
    const isCastingDirector = useSelector(isUserCastingDirectorSelector);
    const isAdmin = useSelector(isAdminSelector);
    const userPermissions = useSelector(userPermissionsSelector);

    const authModalPromptedRef = useRef(false);

    useEffect(() => {
        if (!isSignedIn && !refreshingToken) {
            dispatch(refreshToken({}));
        }
    }, [isSignedIn, refreshingToken, dispatch]);

    useEffect(() => {
        if (!showAuthModal) {
            authModalPromptedRef.current = false;
            return;
        }
        if (isSignedIn) {
            authModalPromptedRef.current = false;
            return;
        }
        if (refreshingToken || storedRefreshToken) return;
        if (!authModalPromptedRef.current) {
            authModalPromptedRef.current = true;
            dispatch(toggleAuthModal(true));
        }
    }, [showAuthModal, isSignedIn, refreshingToken, storedRefreshToken, dispatch]);

    const checkAccess = (type: UserPermissionLevels) => {
        switch (type) {
            case E_USER_PERMISSIONS.JURY:
                return isJuryEnabled || userPermissions.includes(E_USER_PERMISSIONS.JURY);
            case E_USER_PERMISSIONS.CASTING_DIRECTOR:
                return isCastingDirector || userPermissions.includes(E_USER_PERMISSIONS.CASTING_DIRECTOR);
            case 'admin':
                return isAdmin || userPermissions.includes('admin');
            case E_USER_PERMISSIONS.SUPER_ADMIN:
                return isAdmin || userPermissions.includes(E_USER_PERMISSIONS.SUPER_ADMIN);
            case E_USER_PERMISSIONS.TUTOR:
                return (
                    isAdmin ||
                    userPermissions.includes(E_USER_PERMISSIONS.TUTOR) ||
                    userPermissions.includes(E_USER_PERMISSIONS.ADMIN) ||
                    userPermissions.includes(E_USER_PERMISSIONS.SUPER_ADMIN)
                );
            default:
                return userPermissions.includes(type);
        }
    };

    const hasValidAccess = () => {
        if (!accessCheck || !accessType) return true;

        if (Array.isArray(accessType)) {
            return accessType.some(type => checkAccess(type));
        }

        return checkAccess(accessType);
    };

    if (!isSignedIn) {
        // Hold if a refresh is in progress OR if there's a refresh token to try —
        // this covers both mid-session expiry and fresh mounts (e.g. after a view switch).
        // The saga will dispatch logout if the refresh ultimately fails, clearing the
        // refresh token and letting us fall through to the Navigate below.
        if (refreshingToken || storedRefreshToken) return null;
        if (showAuthModal) return null;
        return (
            <Navigate to={`${ROUTES.ESLOGIN}?redirect=${encodeURIComponent(location.pathname + location.search)}`} />
        );
    }

    if (!hasValidAccess()) {
        return <Navigate to={ROUTES.FEED} />;
    }

    return children;
};
